import { CircleNotch, MagnifyingGlass } from "@phosphor-icons/react";
import clsx from "clsx";
import {
  type EmojiPickerListCategoryHeaderProps,
  type EmojiPickerListEmojiProps,
  type EmojiPickerListRowProps,
  EmojiPicker as EmojiPickerPrimitive,
  type EmojiPickerRootProps,
} from "frimousse";

export function EmojiPickerRow({
  children,
  ...props
}: EmojiPickerListRowProps) {
  return (
    <div className="scroll-my-1 px-1" data-slot="emoji-picker-row" {...props}>
      {children}
    </div>
  );
}

function EmojiPickerEmoji({ emoji, ...props }: EmojiPickerListEmojiProps) {
  return (
    <button
      type="button"
      className="flex size-10 items-center justify-center rounded-xl text-2xl-bold data-[active]:bg-black-200 hover:bg-black-100"
      data-slot="emoji-picker-emoji"
      {...props}
    >
      {emoji.emoji}
    </button>
  );
}

function EmojiPickerCategoryHeader({
  category,
  ...props
}: EmojiPickerListCategoryHeaderProps) {
  const { label } = category;

  return (
    <div
      {...props}
      className="bg-white/90 p-3 pt-3.5 text-xs-bold uppercase leading-none text-black-600 backdrop-blur-sm"
      data-slot="emoji-picker-category-header"
    >
      {label}
    </div>
  );
}

export function EmojiPickerRoot(props: EmojiPickerRootProps) {
  return (
    <EmojiPickerPrimitive.Root
      className="isolate flex h-[342px] flex-col overflow-hidden rounded-md text-black-300"
      data-slot="emoji-picker"
      {...props}
    />
  );
}
export function EmojiPickerSearch() {
  return (
    <div
      className="flex items-center gap-2 border-b border-b-black-200 px-3 py-2"
      data-slot="emoji-picker-search-wrapper"
    >
      <div className="relative flex-1">
        <MagnifyingGlass className="absolute left-3 top-1/2 z-10 size-5 shrink-0 -translate-y-1/2 fill-black-950" />

        <EmojiPickerPrimitive.Search
          className={clsx([
            "relative inline-flex w-full items-center justify-center border border-black-100 bg-black-100 pl-9 text-black-950 outline-2 transition-[background,border] has-[:disabled]:cursor-not-allowed has-[:focus-visible]:border-purple-400 has-[:focus-visible]:bg-white has-[:disabled]:text-black-400 has-[:focus-visible]:outline-none has-[:focus-visible]:outline-offset-0 group-data-[error=true]:border-red-400 group-data-[error=true]:bg-white group-data-[error=true]:has-[:focus-visible]:outline-red-300 hover:border-black-200 hover:bg-black-200 has-[:focus-visible]:hover:bg-white [&_input]:placeholder:text-black-500 [&_svg]:text-black-950 ",
            "[&_input]:placeholder:text-black-500",
            "hover:border-black-200 hover:bg-black-200",
            "h-10 gap-2 rounded-full p-3 text-sm-medium focus:border-purple-400 focus:bg-white focus:outline-none [&_svg]:size-5",
          ])}
          data-slot="emoji-picker-search"
        />
      </div>
    </div>
  );
}

export function EmojiPickerContent() {
  return (
    <EmojiPickerPrimitive.Viewport
      className="outline-hidden relative flex-1"
      data-slot="emoji-picker-viewport"
    >
      <EmojiPickerPrimitive.Loading
        className="absolute inset-0 flex items-center justify-center text-black-400"
        data-slot="emoji-picker-loading"
      >
        <CircleNotch className="size-4 animate-spin" />
      </EmojiPickerPrimitive.Loading>

      <EmojiPickerPrimitive.Empty
        className="absolute inset-0 flex items-center justify-center text-xl-bold text-black-400"
        data-slot="emoji-picker-empty"
      >
        No emoji found
      </EmojiPickerPrimitive.Empty>

      <EmojiPickerPrimitive.List
        className="select-none pb-1"
        components={{
          Row: EmojiPickerRow,
          Emoji: EmojiPickerEmoji,
          CategoryHeader: EmojiPickerCategoryHeader,
        }}
        data-slot="emoji-picker-list"
      />
    </EmojiPickerPrimitive.Viewport>
  );
}

export function EmojiPickerFooter() {
  const emojiSkinPicker = (
    <EmojiPickerPrimitive.SkinToneSelector
      emoji="👋"
      className="ml-auto size-9 rounded-xl bg-black-100 text-xl-bold"
    />
  );

  return (
    <div
      className="max-w-(--frimousse-viewport-width) flex w-full min-w-0 items-center gap-2 border-t border-t-black-200 p-2"
      data-slot="emoji-picker-footer"
    >
      <EmojiPickerPrimitive.ActiveEmoji>
        {({ emoji }) =>
          emoji ? (
            <>
              <div className="flex flex-row items-center gap-2">
                <span className="flex size-7 flex-none items-center justify-center text-2xl-bold">
                  {emoji.emoji}
                </span>

                <span className="max-w-[20ch] truncate text-xs-medium text-black-500">
                  {emoji.label}
                </span>
              </div>

              {emojiSkinPicker}
            </>
          ) : (
            <div className="flex w-full items-center">
              <span className="flex h-7 items-center truncate text-xs-medium capitalize text-black-500">
                <span className="mr-2 text-2xl-bold">😊</span> What&apos;s your
                mood?
              </span>

              {emojiSkinPicker}
            </div>
          )
        }
      </EmojiPickerPrimitive.ActiveEmoji>
    </div>
  );
}
